import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FileScan,
  Mail,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Workflow,
} from 'lucide-react';

import {
  deleteMailboxRoute,
  getMailAutomationSettings,
  getMailboxRoutes,
  getWorkflowsCollection,
  MailAutomationSettings,
  MailboxRoute,
  saveMailAutomationSettings,
  saveMailboxRoute,
} from '$api/WorkflowsAPI';
import { AdminShell } from '$layouts/v2/AdminShell';
import { Modal } from '$layouts/v2/components/Modal';
import { Stack } from '$layouts/v2/components/Stack';
import { useAccessToken } from '$src/authConfig';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const environment =
  process.env.VITE_OTAP_ENVIRONMENT?.toUpperCase() ?? 'CURRENT';

type RouteEditorProps = {
  route?: MailboxRoute;
  workflows: { workflowId: string; title: string }[];
  onClose: () => void;
  onSaved: () => void;
};

function RouteEditor({ route, workflows, onClose, onSaved }: RouteEditorProps) {
  const { token } = useAccessToken();
  const [mailbox, setMailbox] = useState(route?.mailbox ?? '');
  const [workflowId, setWorkflowId] = useState(route?.workflowId ?? '');
  const [enabled, setEnabled] = useState(route?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const normalizedMailbox = mailbox.trim().toLowerCase();
  const mailboxError = normalizedMailbox && !EMAIL_RE.test(normalizedMailbox);

  const save = async () => {
    if (!token || !normalizedMailbox || mailboxError || !workflowId) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await saveMailboxRoute(token, {
        mailbox: normalizedMailbox,
        workflowId,
        enabled,
      });
      onSaved();
    } catch (error_) {
      setError(String(error_));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={route ? 'Edit mailbox route' : 'Add mailbox route'}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !normalizedMailbox || Boolean(mailboxError) || !workflowId || busy
            }
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save route'}
          </Button>
        </>
      }
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        Incoming email for this mailbox will start the selected workflow while
        the mailbox is enabled.
      </Typography>
      <TextField
        autoFocus={!route}
        label="Mailbox"
        placeholder="dummy@rotra.nl"
        value={mailbox}
        disabled={Boolean(route)}
        onChange={(event) => setMailbox(event.target.value)}
        error={Boolean(mailboxError)}
        helperText={mailboxError ? 'Enter a valid email address.' : ' '}
      />
      <TextField
        select
        label="Workflow"
        value={workflowId}
        onChange={(event) => setWorkflowId(event.target.value)}
        helperText="Only workflows that start from email are available."
      >
        {workflows.map((workflow) => (
          <MenuItem key={workflow.workflowId} value={workflow.workflowId}>
            {workflow.title} ({workflow.workflowId})
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
        }
        label="Scrape new email from this mailbox"
      />
      {error && <Alert severity="error">{error}</Alert>}
    </Modal>
  );
}

export default function MailAutomationSettingsPage() {
  const { token } = useAccessToken();
  const [settings, setSettings] = useState<MailAutomationSettings | null>(null);
  const [routes, setRoutes] = useState<MailboxRoute[] | null>(null);
  const [workflows, setWorkflows] = useState<
    { workflowId: string; title: string }[]
  >([]);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [routeSaving, setRouteSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [editorRoute, setEditorRoute] = useState<MailboxRoute | undefined>();
  const [editorOpen, setEditorOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      return;
    }

    setError(null);
    try {
      const [settingsData, routeData, workflowData] = await Promise.all([
        getMailAutomationSettings(token),
        getMailboxRoutes(token),
        getWorkflowsCollection(token),
      ]);
      setSettings(settingsData);
      setRoutes(
        routeData.toSorted((a, b) => a.mailbox.localeCompare(b.mailbox)),
      );
      setWorkflows(
        workflowData
          .filter((workflow) => workflow.trigger === 'email')
          .map(({ workflowId, title }) => ({ workflowId, title })),
      );
    } catch (error_) {
      setError(String(error_));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const groupedRoutes = useMemo(() => {
    const groups = new Map<string, MailboxRoute[]>();
    for (const route of routes ?? []) {
      groups.set(route.workflowId, [
        ...(groups.get(route.workflowId) ?? []),
        route,
      ]);
    }

    return [...groups]
      .map(([workflowId, mailboxRoutes]) => ({
        workflowId,
        title:
          workflows.find((workflow) => workflow.workflowId === workflowId)
            ?.title ?? workflowId,
        routes: mailboxRoutes.toSorted((a, b) =>
          a.mailbox.localeCompare(b.mailbox),
        ),
      }))
      .toSorted((a, b) => a.title.localeCompare(b.title));
  }, [routes, workflows]);

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorRoute(undefined);
  };

  const toggleAttachmentParsing = async (enabled: boolean) => {
    if (!token || !settings) {
      return;
    }

    const previous = settings;
    setSettings({ ...settings, attachmentParsingEnabled: enabled });
    setSettingsSaving(true);
    setSavedMessage(null);
    setError(null);

    try {
      setSettings(
        await saveMailAutomationSettings(token, {
          attachmentParsingEnabled: enabled,
        }),
      );
      setSavedMessage('Attachment parsing setting saved.');
    } catch (error_) {
      setSettings(previous);
      setError(String(error_));
    } finally {
      setSettingsSaving(false);
    }
  };

  const toggleMailbox = async (route: MailboxRoute, enabled: boolean) => {
    if (!token || !routes) {
      return;
    }

    const previous = routes;
    setRoutes(
      routes.map((item) =>
        item.id === route.id ? { ...item, enabled } : item,
      ),
    );
    setRouteSaving(route.id);
    setSavedMessage(null);
    setError(null);

    try {
      const saved = await saveMailboxRoute(token, {
        mailbox: route.mailbox,
        workflowId: route.workflowId,
        enabled,
      });
      setRoutes(
        (current) =>
          current?.map((item) => (item.id === saved.id ? saved : item)) ?? null,
      );
      setSavedMessage(
        enabled
          ? `${route.mailbox} enabled. Its subscription will be active within 30 minutes.`
          : `${route.mailbox} disabled. New email will be skipped immediately.`,
      );
    } catch (error_) {
      setRoutes(previous);
      setError(String(error_));
    } finally {
      setRouteSaving(null);
    }
  };

  const removeMailbox = async (route: MailboxRoute) => {
    if (
      !token ||
      !globalThis.confirm(`Remove the workflow route for ${route.mailbox}?`)
    ) {
      return;
    }

    setSavedMessage(null);
    setError(null);
    try {
      await deleteMailboxRoute(token, route.mailbox);
      await load();
      setSavedMessage(`${route.mailbox} removed.`);
    } catch (error_) {
      setError(String(error_));
    }
  };

  return (
    <AdminShell
      title="Mail Automation Settings"
      titleIcon={<Settings2 size={22} />}
      actions={
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setEditorOpen(true)}
          disabled={workflows.length === 0}
        >
          Add mailbox
        </Button>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        These settings apply only to the {environment} environment.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {savedMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSavedMessage(null)}
        >
          {savedMessage}
        </Alert>
      )}

      <Stack spacing={2}>
        <Card>
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1.5,
                bgcolor: 'action.selected',
                color: 'secondary.main',
              }}
            >
              <FileScan size={20} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>
                Attachment parsing
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Extract text and markdown from supported incoming email
                attachments.
              </Typography>
            </Box>
            {settings && (
              <Chip
                size="small"
                color={
                  settings.attachmentParsingEnabled ? 'success' : 'default'
                }
                label={
                  settings.attachmentParsingEnabled ? 'Enabled' : 'Disabled'
                }
              />
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            {settings ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.attachmentParsingEnabled}
                      disabled={settingsSaving}
                      onChange={(event) =>
                        void toggleAttachmentParsing(event.target.checked)
                      }
                    />
                  }
                  label="Parse incoming email attachments"
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                  }}
                >
                  When disabled, new emails are published without downloading or
                  queuing their attachments. Attachments already waiting in the
                  queue are skipped before Document Intelligence is called. A
                  parse already in progress may still finish.
                </Typography>
                {settings.updatedAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      display: 'block',
                      mt: 2,
                    }}
                  >
                    Last changed {new Date(settings.updatedAt).toLocaleString()}
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={20} />
                <Typography
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Loading settings…
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        <Card>
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1.5,
                bgcolor: 'action.selected',
                color: 'secondary.main',
              }}
            >
              <Mail size={20} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Mailbox routing</Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Define which mailboxes are scraped and which workflow handles
                their incoming email.
              </Typography>
            </Box>
            {routes && (
              <Chip
                size="small"
                variant="outlined"
                label={`${
                  routes.filter((route) => route.enabled).length
                } enabled`}
              />
            )}
          </Box>
          <Divider />
          <Alert severity="info" sx={{ m: 2, mb: 0 }}>
            Disabling a mailbox skips new email immediately. Graph subscription
            changes can take up to 30 minutes, and email received while disabled
            is not processed later.
          </Alert>
          {workflows.length === 0 && routes !== null && (
            <Alert severity="info" sx={{ m: 2, mb: 0 }}>
              Create an email-triggered workflow before connecting a mailbox.
            </Alert>
          )}
          {routes === null && (
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography
                sx={{
                  color: 'text.secondary',
                }}
              >
                Loading mailboxes…
              </Typography>
            </Box>
          )}
          {routes?.length === 0 && (
            <Stack
              spacing={1}
              alignItems="center"
              color="text.secondary"
              sx={{ py: 7, textAlign: 'center' }}
            >
              <Mail size={28} />
              <Typography
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                }}
              >
                No mailbox routes yet
              </Typography>
              <Typography variant="body2">
                Add a mailbox and choose the workflow that should run for its
                incoming email.
              </Typography>
            </Stack>
          )}
          {routes !== null && routes.length > 0 && (
            <Stack divider={<Divider flexItem />} sx={{ mt: 2 }}>
              {groupedRoutes.map((group) => (
                <Box key={group.workflowId} sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1.25 }}
                  >
                    <Workflow size={16} />
                    <Typography sx={{ fontWeight: 600 }}>
                      {group.title}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${group.routes.length} ${
                        group.routes.length === 1 ? 'mailbox' : 'mailboxes'
                      }`}
                    />
                  </Stack>
                  <Stack spacing={0.5}>
                    {group.routes.map((route) => (
                      <Stack
                        key={route.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ py: 0.5, pl: 0.25 }}
                      >
                        <Mail
                          size={15}
                          opacity={route.enabled ? undefined : 0.45}
                        />
                        <Typography
                          sx={{
                            flexGrow: 1,
                            fontWeight: 500,
                            color: route.enabled
                              ? 'text.primary'
                              : 'text.disabled',
                          }}
                        >
                          {route.mailbox}
                        </Typography>
                        <Chip
                          size="small"
                          color={route.enabled ? 'success' : 'default'}
                          label={route.enabled ? 'Enabled' : 'Disabled'}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: { xs: 'none', md: 'block' },
                          }}
                        >
                          {new Intl.DateTimeFormat(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          }).format(new Date(route.updatedAt))}
                        </Typography>
                        <Tooltip
                          title={
                            route.enabled ? 'Disable mailbox' : 'Enable mailbox'
                          }
                        >
                          <Switch
                            size="small"
                            checked={route.enabled}
                            disabled={routeSaving !== null}
                            onChange={(event) =>
                              void toggleMailbox(route, event.target.checked)
                            }
                            slotProps={{
                              input: {
                                'aria-label': `${
                                  route.enabled ? 'Disable' : 'Enable'
                                } ${route.mailbox}`,
                              },
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Edit route">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditorRoute(route);
                              setEditorOpen(true);
                            }}
                          >
                            <Pencil size={15} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove route">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void removeMailbox(route)}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Card>
      </Stack>

      {editorOpen && (
        <RouteEditor
          route={editorRoute}
          workflows={workflows}
          onClose={closeEditor}
          onSaved={() => {
            const mailbox = editorRoute?.mailbox;
            closeEditor();
            void load().then(() =>
              setSavedMessage(
                mailbox ? `${mailbox} updated.` : 'Mailbox route added.',
              ),
            );
          }}
        />
      )}
    </AdminShell>
  );
}
